/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Grid } from '@coursera/cds-core';

import { useConsumerMyPageHeader } from 'bundles/composable-page-header-consumer/contexts/ConsumerMyPageHeaderContext';
import EnterpriseOrgScopedMobileSearchBarGridItem from 'bundles/composable-page-header-enterprise/components/EnterpriseOrgScopedMobileSearchBarGridItem';
import {
  getEnterpriseOrgLogoLabel,
  getLinkForOrgHome,
} from 'bundles/composable-page-header-enterprise/utils/enterpriseLogoUtils';
import HelpDropdownGridItem from 'bundles/composable-page-header/components/HelpDropdownGridItem';
import MainNavigationWrapper from 'bundles/composable-page-header/components/MainNavigationWrapper';
import MobileWebChatTriggerGridItem from 'bundles/composable-page-header/components/grid-items/MobileWebChatTriggerGridItem';
import NavAiSparkleGridItem from 'bundles/composable-page-header/components/grid-items/NavAiSparkleGridItem';
import NotificationsLegacyGridItem from 'bundles/composable-page-header/components/grid-items/NotificationsLegacyGridItem';
import CourseraHomeLogo from 'bundles/composable-page-header/components/grid-items/home-logo/CourseraHomeLogo';
import HomeLogoGridItemModule from 'bundles/composable-page-header/components/grid-items/home-logo/HomeLogoGridItemModule';
import MobileNavBarLogo from 'bundles/composable-page-header/components/grid-items/home-logo/MobileNavBarLogo';
import MobileAccountMenuGridItem from 'bundles/composable-page-header/components/grid-items/mobile/MobileAccountMenuGridItem';
import MobileMenuIconGridItem from 'bundles/composable-page-header/components/grid-items/mobile/MobileMenuIconGridItem';
import AccountMenuConfig from 'bundles/composable-page-header/components/mobile-menu-items/AccountMenuConfig';
import useMobileNavIconRowGapStyle from 'bundles/composable-page-header/hooks/useMobileNavIconRowGapStyle';
import LoggedInMobileMenuConfigWithMetaNav from 'bundles/composable-page-header/module-renderers/LoggedInMobileMenuConfigWithMetaNav';

type Props = {
  showDiscoveryElements?: boolean;
  showSearch?: boolean;
  additionalRightItems?: React.ReactNode;
};

const styles = {
  gridItemContainer: css`
    display: flex;
    align-items: center;
  `,
};

const OrgScopedEnterpriseLearnerOnConsumerMobileMainNav = ({
  showDiscoveryElements = true,
  showSearch = true,
  additionalRightItems,
}: Props) => {
  const {
    enrolledDegreePrograms,
    enrolledEnterprisePrograms,
    isCourseraPlusLearner,
    chosenDegree,
    hasEnrolledPrograms,
    naptimeManagedThirdPartyOrganizations,
    hasCheatingIncidents,
    canViewEducatorAdminLink,
    courseraPlusSubscriptionCycle,
    naptimeChosenOrganization,
    naptimeOrganizationsWithProgramEnrollments,
  } = useConsumerMyPageHeader();
  const mobileNavIconRowGapStyle = useMobileNavIconRowGapStyle();

  if (!naptimeChosenOrganization) {
    return null;
  }

  const homeLogo = <CourseraHomeLogo key="1" />;

  return (
    <MainNavigationWrapper>
      <Grid container justifyContent="space-between" alignItems="center" wrap="nowrap">
        <Grid item css={styles.gridItemContainer}>
          <MobileMenuIconGridItem
            menuConfig={
              <LoggedInMobileMenuConfigWithMetaNav
                showDiscoveryElements={showDiscoveryElements}
                enrolledDegreePrograms={enrolledDegreePrograms}
                enrolledEnterprisePrograms={enrolledEnterprisePrograms}
                isCourseraPlusLearner={isCourseraPlusLearner}
                chosenDegree={chosenDegree}
                hasEnrolledPrograms={!!hasEnrolledPrograms}
                naptimeManagedThirdPartyOrganizations={naptimeManagedThirdPartyOrganizations}
                hasCheatingIncidents={hasCheatingIncidents}
                canViewEducatorAdminLink={canViewEducatorAdminLink}
                courseraPlusSubscriptionCycle={courseraPlusSubscriptionCycle}
              />
            }
            homeLogo={
              <HomeLogoGridItemModule
                href={getLinkForOrgHome(naptimeChosenOrganization)}
                logos={[homeLogo]}
                ariaLabel={getEnterpriseOrgLogoLabel(naptimeChosenOrganization)}
              />
            }
          />
          <MobileNavBarLogo
            href={getLinkForOrgHome(naptimeChosenOrganization)}
            logos={[homeLogo]}
            ariaLabel={getEnterpriseOrgLogoLabel(naptimeChosenOrganization)}
          />
        </Grid>
        <Grid item css={[styles.gridItemContainer, mobileNavIconRowGapStyle]}>
          <MobileWebChatTriggerGridItem />
          <HelpDropdownGridItem disableChatSsr={true} />
          <NavAiSparkleGridItem />
          <NotificationsLegacyGridItem />
          {showSearch && showDiscoveryElements && (
            <EnterpriseOrgScopedMobileSearchBarGridItem
              thirdPartyOrganizationSlug={naptimeChosenOrganization?.slug}
              enrolledEnterprisePrograms={enrolledEnterprisePrograms}
            />
          )}
          <MobileAccountMenuGridItem
            isCourseraPlusLearner={!!isCourseraPlusLearner}
            menuConfig={
              <AccountMenuConfig
                naptimeManagedThirdPartyOrganizations={naptimeManagedThirdPartyOrganizations}
                canViewEducatorAdminLink={canViewEducatorAdminLink}
                hasCheatingIncidents={hasCheatingIncidents}
                currentOrganization={naptimeChosenOrganization}
                currentDegree={chosenDegree}
                degrees={enrolledDegreePrograms}
                organizations={naptimeOrganizationsWithProgramEnrollments}
              />
            }
          />
          {additionalRightItems}
        </Grid>
      </Grid>
    </MainNavigationWrapper>
  );
};

export default OrgScopedEnterpriseLearnerOnConsumerMobileMainNav;
